/**
 * ToggleSwitch 滑动开关组件
 *
 * 用于切换布尔状态的滑动开关，支持：
 * - 自定义大小
 * - 禁用状态
 * - 无障碍访问
 */

import React from 'react';

export interface ToggleSwitchProps {
  /** 当前状态 */
  checked: boolean;
  /** 状态变更回调 */
  onChange: (checked: boolean) => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 大小 */
  size?: 'sm' | 'md';
  /** 无障碍标签 */
  ariaLabel?: string;
}

const SIZE_CLASSES = {
  sm: {
    wrapper: 'w-8 h-4',
    thumb: 'w-3 h-3',
    translate: 'translate-x-4'
  },
  md: {
    wrapper: 'w-10 h-5',
    thumb: 'w-4 h-4',
    translate: 'translate-x-5'
  }
} as const;

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  size = 'sm',
  ariaLabel = '切换开关'
}) => {
  const sizeClasses = SIZE_CLASSES[size];

  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`
        relative inline-flex items-center shrink-0 rounded-full
        transition-colors duration-150 ease-in-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-paper-500 focus-visible:ring-offset-2
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${sizeClasses.wrapper}
        ${checked ? 'bg-ink-800' : 'bg-gray-300'}
      `}
    >
      <span
        className={`
          ${sizeClasses.thumb}
          pointer-events-none inline-block rounded-full bg-white shadow-md
          transform transition-transform duration-150 ease-in-out
          ${checked ? sizeClasses.translate : 'translate-x-0.5'}
        `}
      />
    </button>
  );
};

export default ToggleSwitch;
